import { Button, Checkbox, Drawer, Input, Tooltip, message } from "antd";
import { LuCircleHelp } from "react-icons/lu";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  COMMUNICATION_CHANNEL_OPTIONS,
  TIP_TONE_LIMIT_MESSAGE,
  TIP_TONE_MAX_LENGTH,
} from "../../../../helpers/quickTipsAndTonesHelper";
import { AI_RESPONSE_COPY, COMMUNICATION_CHANNEL, SUGGESTION_TYPE } from "./constants";

/**
 * Add/Edit form for a custom Quick Tip or Tone (EAI-1497).
 * Persisted via the AI-suggestion mutations (create/update) by the caller.
 * Rendered as a nested drawer above the Customize AI Response drawer.
 *
 * Channel handling follows the admin SuggestionFormDrawer: on Add the picker
 * decides which use case(s) the item is created under; on Edit the channel is
 * shown read-only, since an update cannot move a row to another use case.
 *
 * @param {{
 *   open: boolean,
 *   mode: "add"|"edit",
 *   suggestionType: "QUICK_TIPS"|"QUICK_TONES",
 *   initialValue?: { tip?: string, description?: string, channels?: string[] },
 *   onSubmit: (values: { tip: string, description: string, channels: string[] }) => void,
 *   onClose: () => void,
 * }} props
 *   `channels` are COMMUNICATION_CHANNEL values ("Text" / "Email").
 */
export default function CustomItemForm({
  open,
  mode,
  suggestionType,
  initialValue,
  submitting,
  channelSelectable,
  onSubmit,
  onClose,
}) {
  const isTip = suggestionType === SUGGESTION_TYPE.TIPS;
  const isEdit = mode === "edit";
  const noun = isTip ? "tip" : "tone";

  const [tip, setTip] = useState("");
  const [description, setDescription] = useState("");
  const [channels, setChannels] = useState([COMMUNICATION_CHANNEL.TEXT]);

  useEffect(() => {
    if (open) {
      setTip(initialValue?.tip ?? "");
      setDescription(initialValue?.description ?? "");
      setChannels(
        initialValue?.channels?.length ? initialValue.channels : [COMMUNICATION_CHANNEL.TEXT]
      );
    }
  }, [open, initialValue]);

  const toggleChannel = (channel, checked) =>
    setChannels(prev =>
      checked ? [...new Set([...prev, channel])] : prev.filter(c => c !== channel)
    );

  const handleSubmit = () => {
    if (!tip.trim()) {
      message.error(`Please enter the ${noun} name`);
      return;
    }
    if (tip.trim().length > TIP_TONE_MAX_LENGTH) {
      message.error(TIP_TONE_LIMIT_MESSAGE);
      return;
    }
    if (channelSelectable && !isEdit && !channels.length) {
      message.error(`Please select where this ${noun} should be used`);
      return;
    }
    onSubmit({
      tip: tip.trim(),
      description: description.trim(),
      // Text-only surfaces (e.g. Review) always persist against the Text channel.
      channels: channelSelectable ? channels : [COMMUNICATION_CHANNEL.TEXT],
    });
  };

  const title = `${isEdit ? "Edit" : "Add"} ${noun}`;
  const instructionsHelp = AI_RESPONSE_COPY.CUSTOM_INSTRUCTIONS_HELP.replace("{noun}", noun);

  // Save is enabled only when the form is valid AND, in edit mode, something
  // actually changed — no saving an unchanged item. The channel is not editable
  // on an existing row, so it never makes the form dirty.
  const nameEntered = tip.trim().length > 0;
  const nameTooLong = tip.trim().length > TIP_TONE_MAX_LENGTH;
  const channelSelected = !channelSelectable || isEdit || channels.length > 0;
  const isDirty =
    tip.trim() !== (initialValue?.tip ?? "").trim() ||
    description.trim() !== (initialValue?.description ?? "").trim();
  const canSave = nameEntered && !nameTooLong && channelSelected && (!isEdit || isDirty);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={440}
      // The panel this opens over is an antd Popover (z-index ~1030). Without a
      // higher z-index the Drawer (default ~1000) renders BEHIND the panel and
      // the two overlap on narrow screens. Sit above the popover (and below the
      // delete-confirm Modal at 1100) so the form cleanly overlays the panel.
      zIndex={1060}
      title={title}
      footer={
        <div className="flex flex-row gap-2">
          <Button type="primary" loading={submitting} disabled={!canSave} onClick={handleSubmit}>
            Save
          </Button>
          <Button disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-grey-700">
            <span className="text-red-500">*</span> {isTip ? "Tip" : "Tone"} name
          </span>
          <Input
            value={tip}
            onChange={e => setTip(e.target.value)}
            placeholder={`Write the ${noun} name`}
            count={{ show: true, max: TIP_TONE_MAX_LENGTH }}
            status={nameTooLong ? "error" : undefined}
          />
          {nameTooLong && <span className="text-xs text-red-500">{TIP_TONE_LIMIT_MESSAGE}</span>}
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-sm text-grey-700">
            {AI_RESPONSE_COPY.ADDITIONAL_NOTES}
            <Tooltip title={instructionsHelp}>
              <LuCircleHelp className="text-grey-400 text-sm" />
            </Tooltip>
            <span className="text-grey-400">{AI_RESPONSE_COPY.ADDITIONAL_NOTES_MARK}</span>
          </span>
          <Input.TextArea
            value={description}
            onChange={e => setDescription(e.target.value)}
            autoSize={{ minRows: 3 }}
            placeholder={instructionsHelp}
          />
        </div>

        {channelSelectable && isEdit && (
          // A record's channel is fixed once created — shown, not editable, so
          // the drawer still tells you which channel you are editing.
          <div className="flex flex-col gap-2">
            <span className="text-sm text-grey-700">Where this {noun} is used</span>
            {channels.map(channel => {
              const option = COMMUNICATION_CHANNEL_OPTIONS.find(o => o.value === channel);
              return (
                <div key={channel} className="flex flex-col gap-0.5">
                  <span className="text-sm text-grey-700">{option?.label || channel}</span>
                  <span className="text-xs text-grey-500">{option?.description}</span>
                </div>
              );
            })}
          </div>
        )}

        {channelSelectable && !isEdit && (
          <div className="flex flex-col gap-2">
            <span className="text-sm text-grey-700">
              {AI_RESPONSE_COPY.CUSTOM_WHERE_USED.replace("{noun}", noun)}
            </span>
            {COMMUNICATION_CHANNEL_OPTIONS.map(option => (
              <div key={option.value} className="flex flex-col gap-0.5">
                <Checkbox
                  checked={channels.includes(option.value)}
                  onChange={e => toggleChannel(option.value, e.target.checked)}
                >
                  {option.label}
                </Checkbox>
                <span className="ml-6 text-xs text-grey-500">{option.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

CustomItemForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]).isRequired,
  suggestionType: PropTypes.oneOf([SUGGESTION_TYPE.TIPS, SUGGESTION_TYPE.TONES]).isRequired,
  initialValue: PropTypes.shape({
    tip: PropTypes.string,
    description: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.string),
  }),
  submitting: PropTypes.bool,
  channelSelectable: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CustomItemForm.defaultProps = {
  initialValue: null,
  submitting: false,
  channelSelectable: true,
};
